import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search as SearchIcon, Phone, Mail, ChevronRight, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
    active: { label: 'Active', cls: 'bg-[#047857] text-white' },
    waiting_list: { label: 'Waiting list', cls: 'bg-[#D97706] text-white' },
    pending_enrolment: { label: 'Pending', cls: 'bg-[#2563EB] text-white' },
    unregistered: { label: 'Unregistered', cls: 'bg-[#6B7280] text-white' },
    didnt_renew: { label: "Didn't renew", cls: 'bg-[#B91C1C] text-white' },
    discontinued: { label: 'Discontinued', cls: 'bg-[#374151] text-white' },
    inactive: { label: 'Inactive', cls: 'bg-[#9CA3AF] text-white' },
};

const GuardianSearch = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const base = pathname.startsWith('/manager') ? '/manager' : '/admin';
    const [q, setQ] = useState('');
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (q.trim().length < 2) { setRows(null); return; }
        const t = setTimeout(() => {
            setBusy(true);
            axios.get(`${API_URL}/api/parents/search`, { params: { q: q.trim() }, withCredentials: true }).then(r => setRows(r.data || [])).catch(() => setRows([])).finally(() => setBusy(false));
        }, 300);
        return () => clearTimeout(t);
    }, [q]);
    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="guardian-search-page">
                <div><h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2"><SearchIcon className="h-6 w-6 text-[#064E3B]" />Search</h1>
                    <p className="text-[#4B5563]">Find any guardian — with or without students, waiting list, pending, unregistered or didn't renew — by mobile, email, name or code</p></div>
                <div className="relative max-w-2xl">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6B7280]" />
                    <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Type part of a mobile number, email, name or guardian code…" className="pl-10 h-12 text-base" data-testid="guardian-search-input" />
                </div>
                {rows === null ? <p className="text-sm text-[#6B7280]" data-testid="guardian-search-hint">{busy ? 'Searching…' : 'Enter at least 2 characters.'}</p>
                    : rows.length === 0 ? <Card className="card-surface"><CardContent className="p-8 text-center text-[#6B7280]" data-testid="guardian-search-empty">No guardian matches “{q}”.</CardContent></Card>
                        : <div className="space-y-2" data-testid="guardian-search-results">
                            <p className="text-xs text-[#6B7280]">{rows.length} guardian{rows.length === 1 ? '' : 's'} found{rows.length === 60 ? ' (showing first 60 — refine your search)' : ''}</p>
                            {rows.map(p => (
                                <Card key={p.id} className="card-surface hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`${base}/parents/${p.id}`)} data-testid={`guardian-result-${p.id}`}>
                                    <CardContent className="p-4 flex flex-wrap items-center gap-4">
                                        <div className="min-w-[220px]">
                                            <p className="font-semibold text-[#1F2937]">{p.first_name} {p.last_name} <span className="font-mono text-xs text-[#6B7280]">{p.reg_code}</span></p>
                                            {(p.guardian2_first_name || p.guardian2_last_name) && <p className="text-xs text-[#4B5563]">Guardian 2: {p.guardian2_first_name} {p.guardian2_last_name}</p>}
                                            <p className="text-xs text-[#4B5563] flex flex-wrap gap-x-3 mt-0.5">
                                                {p.mobile && <span className="inline-flex items-center gap-1 tabular-nums"><Phone className="h-3 w-3" />{p.mobile}</span>}
                                                {p.guardian2_mobile && <span className="inline-flex items-center gap-1 tabular-nums"><Phone className="h-3 w-3" />{p.guardian2_mobile}</span>}
                                                {p.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{p.email}</span>}
                                            </p>
                                        </div>
                                        <div className="flex-1 flex flex-wrap gap-1.5">
                                            {p.no_children ? <Badge variant="outline" className="text-[#6B7280] gap-1"><Users className="h-3 w-3" />No students registered</Badge>
                                                : p.students.map(s => <Badge key={s.id} className={`${(STATUS[s.status] || STATUS.inactive).cls} gap-1 font-normal`} data-testid={`guardian-result-student-${s.id}`}>{s.name}{s.class_name ? ` · ${s.class_name}` : ''} — {(STATUS[s.status] || STATUS.inactive).label}</Badge>)}
                                        </div>
                                        <ChevronRight className="h-5 w-5 text-[#9CA3AF]" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>}
            </div>
        </DashboardLayout>
    );
};

export default GuardianSearch;
